import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { TankFilterValues } from '@components/Common/EntryFilters/TankSelectorControlled';
import { DateFilterValues } from '@components/Common/EntryFilters/DateFilterControlled';
import { ShipmentType } from '@api/queries/inventory';

// URL param keys
const PARAM_TERMINAL = 'terminal';
const PARAM_TANK = 'tank';
const PARAM_CUSTOMER = 'customer';
const PARAM_FROM = 'from';
const PARAM_TO = 'to';
const PARAM_SHIPMENT_TYPE = 'shipmentType';

export interface BillingReportFilters {
  terminalId: string | null;
  tankId: string | null;
  customerId: string | null;
  startDate: string | null;
  endDate: string | null;
  shipmentType: ShipmentType | null;
}

export interface UseBillingReportFiltersResult {
  filters: BillingReportFilters;
  tankFilter: TankFilterValues;
  dateFilter: DateFilterValues;
  setTankFilter: (value: TankFilterValues) => void;
  setCustomerId: (customerId: string | null) => void;
  setDateFilter: (value: DateFilterValues) => void;
  setShipmentType: (shipmentType: ShipmentType | null) => void;
  clearFilters: () => void;
}

/**
 * Hook to manage billing report filters via URL search params.
 *
 * URL format: /reports/billing?terminal=xxx&tank=xxx&customer=xxx&from=xxx&to=xxx&shipmentType=1
 */
export function useBillingReportFilters(): UseBillingReportFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read values from URL
  const filters = useMemo<BillingReportFilters>(() => {
    const terminalId = searchParams.get(PARAM_TERMINAL);
    const tankId = searchParams.get(PARAM_TANK);
    const customerId = searchParams.get(PARAM_CUSTOMER);
    const startDate = searchParams.get(PARAM_FROM);
    const endDate = searchParams.get(PARAM_TO);
    const shipmentTypeStr = searchParams.get(PARAM_SHIPMENT_TYPE);
    const shipmentType = shipmentTypeStr !== null ? (parseInt(shipmentTypeStr, 10) as ShipmentType) : null;

    return {
      terminalId,
      tankId,
      customerId,
      startDate,
      endDate,
      shipmentType,
    };
  }, [searchParams]);

  // Tank filter for the controlled component
  const tankFilter = useMemo<TankFilterValues>(() => ({
    terminalId: filters.terminalId,
    tankId: filters.tankId,
  }), [filters.terminalId, filters.tankId]);

  // Date filter for the controlled component
  const dateFilter = useMemo<DateFilterValues>(() => ({
    startDate: filters.startDate,
    endDate: filters.endDate,
  }), [filters.startDate, filters.endDate]);

  // Update tank filter in URL
  const setTankFilter = useCallback((value: TankFilterValues) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      if (value.terminalId) {
        newParams.set(PARAM_TERMINAL, value.terminalId);
      } else {
        newParams.delete(PARAM_TERMINAL);
      }

      if (value.tankId) {
        newParams.set(PARAM_TANK, value.tankId);
      } else {
        newParams.delete(PARAM_TANK);
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Update customer in URL
  const setCustomerId = useCallback((customerId: string | null) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      if (customerId) {
        newParams.set(PARAM_CUSTOMER, customerId);
      } else {
        newParams.delete(PARAM_CUSTOMER);
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Update date filter in URL
  const setDateFilter = useCallback((value: DateFilterValues) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      if (value.startDate) {
        newParams.set(PARAM_FROM, value.startDate);
      } else {
        newParams.delete(PARAM_FROM);
      }

      if (value.endDate) {
        newParams.set(PARAM_TO, value.endDate);
      } else {
        newParams.delete(PARAM_TO);
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Update shipment type in URL
  const setShipmentType = useCallback((shipmentType: ShipmentType | null) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      if (shipmentType !== null) {
        newParams.set(PARAM_SHIPMENT_TYPE, shipmentType.toString());
      } else {
        newParams.delete(PARAM_SHIPMENT_TYPE);
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  return {
    filters,
    tankFilter,
    dateFilter,
    setTankFilter,
    setCustomerId,
    setDateFilter,
    setShipmentType,
    clearFilters,
  };
}

export default useBillingReportFilters;
